import { useCallback, useState } from "react";
import { Button, Typography, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import SelectionFormFields from "../../components/selection/SelectionFormFields";
import SelectionSharePanel from "../../components/selection/SelectionSharePanel";
import copyToClipboard from "../../utils/copy";
import { buildSelectionUrl } from "../../utils/selectionToken";
import { createSelection } from "../../utils/selectionAdminApi";
import { emptySelectionFormValues, validateSelectionForm } from "../../utils/selectionForm";
import { useTranslation } from "../../i18n";
import "./selection.css";

const { Title, Paragraph } = Typography;

function SelectionAdmin() {
    const { t } = useTranslation();
    const [values, setValues] = useState(emptySelectionFormValues);
    const [generatedUrl, setGeneratedUrl] = useState("");
    const [generatedAlbumName, setGeneratedAlbumName] = useState("");
    const [expiryPreview, setExpiryPreview] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    const handleField = useCallback((field, value) => {
        setValues((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleGenerate = useCallback(async () => {
        setError("");
        setGeneratedUrl("");
        setGeneratedAlbumName("");
        setExpiryPreview("");

        let normalized;
        try {
            normalized = validateSelectionForm(values, t);
        } catch (err) {
            setError(err.message || t("selection.admin.generateFailed"));
            return;
        }

        setGenerating(true);
        try {
            const created = await createSelection({
                albumPath: normalized.albumPath,
                albumName: normalized.albumName,
                expiry: normalized.expiry,
                consent: normalized.consent,
                downloadAllType: normalized.downloadAllType,
                downloadAllToken: normalized.downloadAllToken,
                archiveMarkdown: normalized.archiveMarkdown,
            });
            setGeneratedUrl(buildSelectionUrl(created.id));
            setGeneratedAlbumName(normalized.albumName);
            setExpiryPreview(values.expiry.format("YYYY-MM-DD HH:mm"));
        } catch (err) {
            setError(err.message || t("selection.admin.generateFailed"));
        } finally {
            setGenerating(false);
        }
    }, [values, t]);

    const handleCopy = useCallback(async () => {
        if (!generatedUrl) {
            return;
        }
        try {
            await copyToClipboard(generatedUrl);
            message.success(t("selection.admin.linkCopied"));
        } catch {
            message.error(t("selection.admin.copyFailed"));
        }
    }, [generatedUrl, t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("selection.admin.title")}</span>
                </div>
                <Paragraph style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                    {t("selection.admin.subtitle")}
                </Paragraph>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("selection.admin.description")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <SelectionFormFields values={values} onChange={handleField} />

                    {error && (
                        <Paragraph type="danger" style={{ marginBottom: "1rem" }}>
                            {error}
                        </Paragraph>
                    )}

                    <div className="delivery-admin-actions">
                        <Button type="primary" loading={generating} onClick={handleGenerate}>
                            {t("selection.admin.generate")}
                        </Button>
                    </div>

                    {generatedUrl && (
                        <div className="delivery-admin-result">
                            <Title level={5}>{t("selection.admin.resultTitle")}</Title>
                            {expiryPreview && (
                                <p className="delivery-admin-preview">
                                    {t("selection.admin.expiresAt", { time: expiryPreview })}
                                </p>
                            )}
                            <div className="delivery-admin-result__url">{generatedUrl}</div>
                            <div className="delivery-admin-actions">
                                <Button type="primary" onClick={handleCopy}>
                                    {t("selection.admin.copyLink")}
                                </Button>
                            </div>

                            <div className="delivery-admin-poster">
                                <Title level={5}>{t("selection.admin.posterTitle")}</Title>
                                <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                                    {t("selection.admin.posterHint")}
                                </Paragraph>
                                <SelectionSharePanel
                                    url={generatedUrl}
                                    albumName={generatedAlbumName}
                                    expiresAtLabel={expiryPreview}
                                    compact
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default SelectionAdmin;
